import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// --- Constants for the visualization ---
const PARTICLE_COUNT = 2000;
const BASE_RADIUS = 0.8;
const SMOOTHING_FACTOR = 0.08;

// --- Helper function for manual color interpolation ---
const lerpColor = (c1:any, c2:any, factor:any) => ({
  r: c1.r + factor * (c2.r - c1.r),
  g: c1.g + factor * (c2.g - c1.g),
  b: c1.b + factor * (c2.b - c1.b),
});

// --- Color constants as RGB objects (0-1 range) ---
const BASE_COLOR = { r: 129 / 255, g: 140 / 255, b: 248 / 255 }; // #818cf8
const PEAK_COLOR = { r: 244 / 255, g: 114 / 255, b: 182 / 255 }; // #f472b6

interface AudioOrbProps {
  stream: MediaStream | null;
}

const AudioOrb: React.FC<AudioOrbProps> = ({ stream }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const visualizerRef = useRef<any>(null);

  useEffect(() => {
    if (!mountRef.current || !stream) return;

    const mount = mountRef.current;

    // --- Audio Analysis Setup ---
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioCtx();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    let source: MediaStreamAudioSourceNode | null = null;

    // Only create a MediaStreamAudioSourceNode if the stream actually has audio tracks.
    if (stream.getAudioTracks && stream.getAudioTracks().length > 0) {
      source = audioContext.createMediaStreamSource(stream);
      source?.connect(analyser);
    } else {
      // No audio track -> use silent analyser fallback (will produce zeros)
      console.warn('AudioOrb: MediaStream has no audio track — using silent analyser fallback.');
    }

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);

    // --- Three.js Scene Setup ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    camera.position.z = 2.5;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mount.appendChild(renderer.domElement);

    // --- Particle System Setup ---
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const colors = new Float32Array(PARTICLE_COUNT * 3);
    const originalPositions = new Float32Array(PARTICLE_COUNT * 3);

    const phi = Math.PI * (3. - Math.sqrt(5.));
    for (let i = 0; i < PARTICLE_COUNT; i++) {
        const y = 1 - (i / (PARTICLE_COUNT - 1)) * 2;
        const radius = Math.sqrt(1 - y * y);
        const theta = phi * i;
        const x = Math.cos(theta) * radius;
        const z = Math.sin(theta) * radius;
        
        const index = i * 3;
        positions[index] = x * BASE_RADIUS;
        positions[index + 1] = y * BASE_RADIUS;
        positions[index + 2] = z * BASE_RADIUS;
        
        originalPositions[index] = x;
        originalPositions[index + 1] = y;
        originalPositions[index + 2] = z;

        colors[index] = BASE_COLOR.r;
        colors[index + 1] = BASE_COLOR.g;
        colors[index + 2] = BASE_COLOR.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const material = new THREE.PointsMaterial({
        size: 0.02,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.9,
        sizeAttenuation: true,
        vertexColors: true
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    visualizerRef.current = {
      smoothedAverage: 0,
    };

    // --- Animation Loop ---
    let animationFrameId: number;
    const animate = () => {
      analyser.getByteFrequencyData(frequencyData);

      let total = 0;
      for (let i = 0; i < frequencyData.length; i++) {
        total += frequencyData[i];
      }
      const averageVolume = total / frequencyData.length;
      
      visualizerRef.current.smoothedAverage += (averageVolume - visualizerRef.current.smoothedAverage) * SMOOTHING_FACTOR;
      
      const positions = particles.geometry.attributes.position.array as Float32Array;
      const colors = particles.geometry.attributes.color.array as Float32Array;
      const time = Date.now() * 0.0005;
      
      const normVolume = visualizerRef.current.smoothedAverage / 255;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const ix = i * 3, iy = i * 3 + 1, iz = i * 3 + 2;
        const ox = originalPositions[ix], oy = originalPositions[iy], oz = originalPositions[iz];

        const overallDisplacement = Math.pow(normVolume, 0.6) * 4.0;
        const organicMotion = Math.sin(oy * 4 + time) * 0.05;

        const freqIndex = Math.floor((Math.abs(oy) + 1) / 2 * (frequencyData.length * 0.1));
        const freqStrength = frequencyData[freqIndex] / 255;
        const freqDeformation = freqStrength * overallDisplacement * 0.8;

        const totalRadius = BASE_RADIUS + overallDisplacement + organicMotion + freqDeformation;

        positions[ix] = ox * totalRadius;
        positions[iy] = oy * totalRadius;
        positions[iz] = oz * totalRadius;
        
        const rawLerp = Math.min((totalRadius - BASE_RADIUS) / 2.0, 1.0);
        const exaggeratedLerp = Math.pow(rawLerp, 0.7);
        const particleColor = lerpColor(BASE_COLOR, PEAK_COLOR, exaggeratedLerp);
        
        colors[ix] = particleColor.r;
        colors[iy] = particleColor.g;
        colors[iz] = particleColor.b;
      }
      
      particles.geometry.attributes.position.needsUpdate = true;
      particles.geometry.attributes.color.needsUpdate = true;
      particles.rotation.y += 0.001;
      particles.rotation.x += 0.0005;

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();

    // --- NEW: ResizeObserver Logic ---
    // This observer will fire whenever the component's container div resizes.
    const resizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
            const { width, height } = entry.contentRect;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        }
    });
    resizeObserver.observe(mount);


    // --- Cleanup ---
    return () => {
      resizeObserver.disconnect(); // Disconnect the observer
      cancelAnimationFrame(animationFrameId);
      audioContext.close();
      if (mount) {
          mount.removeChild(renderer.domElement);
      }
    };
  }, [stream]);

  return <div ref={mountRef} className="absolute inset-0 w-full h-full" />;
};

export default AudioOrb;